/**
 * @license
 * Copyright 2026 Aglyn LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
'use client'

import {
  checkPluginOrgCapacityQuota,
  checkSeatQuota,
  resolveOrgEntitlements,
  resolvePlanComp,
  UNLIMITED,
} from '@aglyn/aglyn'
import { pluginOrgCapacities } from '@aglyn/aglyn/plugin-manager/plugin-org-capacity'
import {
  pluginUsageBands,
  type ResolvedPluginUsageBand,
} from '@aglyn/aglyn/plugin-manager/plugin-usage-axes'
import { AppLink } from '@aglyn/shared-ui-jsx'
import { USAGE_METER_WARNING_PCT } from '@aglyn/shared-ui-jsx/components/usage-meter.component'
import { Alert, Button } from '@mui/material'
import { collection, getCountFromServer } from 'firebase/firestore'
import { useParams } from 'next/navigation'
import { useEffect, useRef, useState } from 'react'
import { useFirestore, useScopeTokens, useUser } from '@aglyn/tenant-feature-instance'
import { authorizedFetch } from '@aglyn/shared-util-http/authorized-token'
import { buildRoute, Route } from '../constants/route-links'
import { updatePaymentMethodHref } from '../utils/update-payment-method-link'
import { useHostId } from '../components/host-id-provider'
import type { MediaStorageBand } from './media/media-storage-copy'
import { useMediaStorageBand } from './media/use-media-storage-band'
import { useOrgScope, useOrgSlug } from '../hooks/use-org-scope'
import { useUrlNamesOrg } from '../hooks/use-secondary-nav'
import useCurrentOrg from '../hooks/use-current-org'

const DISMISS_KEY = 'aglyn-quota-banner-dismissed'
// Versioned AND org-scoped (AGL-1113), suffixed with the orgId at use.
// The v1 key was a single global string holding a RAW member count: it both
// counted site collaborators as managers and leaked one workspace's total to
// the next workspace the user opened. Reusing the old key would keep serving
// that number — and its false "out of team seats" banner — from every session
// that already has it, making the fix look like it did not land.
const SEATS_KEY = 'aglyn-quota-banner-manager-seats-v2'

/**
 * Forget a cached seat count (AGL-1253).
 *
 * A read that failed must not leave the PREVIOUS answer standing. The cache
 * is consulted before the fetch, so a stale entry survives every later render
 * and keeps rendering a number nothing has confirmed — which is how an
 * account whose seat read was being denied ended up looking at "You've
 * reached your team seats limit", an upgrade prompt on false pretences.
 */
function clearSeatCache(cacheKey: string): void {
  try {
    sessionStorage.removeItem(cacheKey)
  } catch {
    // Private-mode storage failures are not worth a broken banner.
  }
}

export interface QuotaWarningsBannerProps {
  /** Overrides the route param; the banner resolves `[hostId]` itself. */
  hostId?: string
}

interface QuotaState {
  label: string
  used: number
  limit: number
  /**
   * A plugin band's row (AGL-2898, AGL-3080) carries the band's id, because
   * its sentence is its own: what happens at a band can differ by plan, and
   * the generic "upgrade to keep adding" is wrong for a band that is sold
   * past.
   */
  key?: string
  /** The band's own sentence, as its declaration words it for this standing. */
  sentence?: string
  /** The sentence names Billing → Usage, so the banner links it. */
  linksUsage?: boolean
}

/** The bands a plugin warns about here, from its compiled declaration. */
type WarnedBand = ResolvedPluginUsageBand & {
  consoleWarning: NonNullable<ResolvedPluginUsageBand['consoleWarning']>
}

const warnedBands = (): WarnedBand[] =>
  pluginUsageBands().filter((band): band is WarnedBand => Boolean(band.consoleWarning))

/**
 * A band's sentence for its standing: approaching it, or at it — and at it,
 * whether the plugin's route reports that the band stops there or bills past
 * it. A route that did not say reads as the wall: promising a charge the plan
 * may not bill is the worse of the two sentences.
 */
function bandSentence(
  warning: WarnedBand['consoleWarning'],
  used: number,
  limit: number,
  stopsAtBand: unknown,
): string {
  if (used < limit) return warning.approach
  return stopsAtBand === false ? warning.reached.bills : warning.reached.stops
}

/**
 * The storage row's sentences (AGL-3479), by who is reading and by what the
 * band does: storage is the WORKSPACE's band — every site's library and the
 * organization's share it (AGL-2075) — so no sentence calls it this site's,
 * and at the band a plan that bills past it is never told uploads stop.
 */
const STORAGE_SENTENCES = {
  orgWide: {
    approach: {
      stops:
        `You're above ${USAGE_METER_WARNING_PCT}% of your workspace's ` +
        'storage — every site and the organization library share it, and ' +
        'uploads stop when it is full.',
      bills:
        `You're above ${USAGE_METER_WARNING_PCT}% of your workspace's ` +
        'storage — every site and the organization library share it, and ' +
        'past it extra storage is billed unless you set a storage cap under ' +
        'Billing → Usage.',
    },
    reached: {
      stops:
        "You've used all of your workspace's storage — new uploads stop " +
        'until you free up space or upgrade.',
      bills:
        "You've used your workspace's included storage — extra storage is " +
        'billed at your plan’s rate unless you set a storage cap under ' +
        'Billing → Usage.',
    },
  },
  scoped: {
    approach: {
      stops:
        `This workspace is above ${USAGE_METER_WARNING_PCT}% of its ` +
        'storage, which every site shares — uploads stop when it is full.',
      bills:
        `This workspace is above ${USAGE_METER_WARNING_PCT}% of its ` +
        'storage, which every site shares — past it, extra storage is ' +
        'billed to the workspace.',
    },
    reached: {
      stops:
        'This workspace has used all of its storage — new uploads stop ' +
        'until space is freed or a workspace admin upgrades.',
      bills:
        'This workspace has used its included storage — extra storage is ' +
        'billed to the workspace.',
    },
  },
} as const

/**
 * The storage row (AGL-3479): the workspace's pooled bytes against its pooled
 * band, both from `/api/media/storage` — `resolveOrgMediaBand`, the function
 * the upload gate calls — and never one site's counter against the per-site
 * figure, which reads a workspace whose other libraries are full as having
 * room, and a single busy site as over a band it shares.
 *
 * No band (a failed read, a viewer the route refuses) or an unlimited one is
 * no row. `hardBand` is the gate's own refusing arm, so "stops" and "bills"
 * follow what an upload past the band actually does on this plan.
 */
function storageQuota(
  band: MediaStorageBand | null,
  scopedViewer: boolean,
): QuotaState | null {
  if (!band || !Number.isFinite(band.allowanceMb) || band.allowanceMb <= 0) {
    return null
  }
  const used = band.usedBytes / (1024 * 1024)
  const limit = band.allowanceMb
  const bills = !band.hardBand
  const words = STORAGE_SENTENCES[scopedViewer ? 'scoped' : 'orgWide']
  return {
    label: 'storage',
    used,
    limit,
    sentence: (used >= limit ? words.reached : words.approach)[
      bills ? 'bills' : 'stops'
    ],
    linksUsage: bills,
  }
}

/**
 * Site-wide quota warnings (AGL-136, grown from the AGL-98 dashboard
 * banner): rendered by DashboardLayout on every host and manage page when
 * any tracked quota — pages, the workspace's storage, datasets, or team
 * seats — crosses 80% (warning) or 100% (error), with an Upgrade link to
 * Billing.
 * Dismissible per browser session. Consistent with the dark-launch rule,
 * workspaces without an explicit plan see nothing.
 *
 * Every action here points at Billing, so for a site collaborator every
 * action is a dead end (AGL-1072): AGL-1032 took Billing out of their console
 * and redirects them off the URL. They keep the warning and lose the button.
 */
export function QuotaWarningsBanner(props: QuotaWarningsBannerProps) {
  const params = useParams<{ hostId?: string }>()
  const hostId = props.hostId ?? useHostId()
  const firestore = useFirestore()
  const orgSlug = useOrgSlug()
  // EXEMPT from `no-unguarded-loading-hook` (AGL-1422). This banner already
  // holds on the stronger condition: every branch of it is downstream of
  // `plan = org?.plan`, and each quota effect starts `if (!plan …) return`,
  // so an undefined `org` produces silence rather than a free-tier warning.
  // The suspension and dunning banners read fields off the same undefined
  // object and render nothing for the same reason. Silence is the correct
  // pending outcome for a component whose entire job is to make claims about
  // the plan, and `ready` would not change a single branch — but it WOULD
  // put a second condition beside `plan` for a reader to reconcile.
  //
  // That argument holds for the case it addresses and MISSED the other one
  // (AGL-1916). `plan` being undefined is not the only way this banner can be
  // wrong: `useCurrentOrg` resolves through `useOrgScope().currentOrg`, which
  // falls back to a remembered selection and then to the user's FIRST org, so
  // on the workspace picker `plan` is perfectly truthy — it just belongs to an
  // org the URL never named. You've reached
  // your team seats limit — upgrade to keep adding above the four unopened
  // workspace cards, naming none of them, with UPGRADE pointing at the
  // fallback org's billing page. A guard against NO answer is not a guard
  // against the WRONG answer.
  // eslint-disable-next-line aglyn/no-unguarded-loading-hook
  const { org, orgId } = useCurrentOrg()
  // Whether the URL itself scopes this page to a workspace (AGL-1130's
  // predicate, and the same one the top bar, user menu, org switcher and
  // `useBranding` already gate their org CLAIMS on). This banner is a claim in
  // exactly that sense — a statement about one org's plan and usage, with a
  // CTA to buy for it — so it belongs on that list and was simply left off it.
  const namesOrg = useUrlNamesOrg()
  const { pathOrgSlug, currentOrg } = useOrgScope()
  // …and a second, sharper check for the case the URL DOES name an org: that
  // the org which answered is the org the URL asked about. `currentOrg` only
  // matches `pathOrgSlug` when the user is a member of that workspace; visit
  // `/some-other-org/...` (a shared link, a stale bookmark, a membership list
  // that has not loaded the row yet) and the scope quietly falls through to
  // the first org instead — same ambient answer, now wearing a URL that
  // appears to justify it.
  //
  // Deliberately a POSITIVE contradiction, not `currentOrg?.slug ===
  // pathOrgSlug`. `slug` is optional on the membership doc, and a legacy row
  // without one must not silence a genuine breach — a quota warning nobody
  // sees is revenue nobody collects, which is a worse trade than the false
  // positive being fixed here. Only a slug that actively disagrees suppresses.
  const wrongOrg = Boolean(
    pathOrgSlug && currentOrg?.slug && currentOrg.slug !== pathOrgSlug,
  )
  const orgInScope = namesOrg && !wrongOrg
  // Org-wide totals are not askable by a site collaborator (AGL-1068). An
  // unfiltered `count()` over an org collection is denied on the QUERY
  // SHAPE for a scoped member — Firestore does not evaluate it per
  // document — so it fails for them no matter what the collection holds.
  // `loaded` matters as much as `orgWide`: this hook reports org-wide
  // while it loads (AGL-1047), so acting on the value alone would fire
  // exactly the denied query this is here to prevent, on first render.
  //
  // The same answer gates the ACTIONS below (AGL-1072): every action in this
  // banner is a link to Billing, which AGL-1032 removed from a collaborator's
  // console and redirects them off. One predicate for both, deliberately —
  // `useOrgReach` answers the same question from the AGL-1032 nav mirror, but
  // a second async source resolving on its own clock could show the Upgrade
  // button for a beat after the counts had already decided the viewer is
  // scoped.
  const { data: user } = useUser()
  const { orgWide: viewerOrgWide, loaded: scopeLoaded } = useScopeTokens(orgId)
  const orgWideViewer = scopeLoaded && viewerOrgWide
  // The two are NOT complements, and the loading window is why. An action
  // withheld for a beat costs an owner one render; the collaborator wording
  // shown for a beat tells that owner to go ask an admin — themselves. So
  // the action waits for "known org-wide" and the rewording waits for the
  // stronger "known scoped".
  const scopedViewer = scopeLoaded && !viewerOrgWide
  const [quotas, setQuotas] = useState<QuotaState[]>([])
  const [dismissed, setDismissed] = useState(true)

  useEffect(() => {
    setDismissed(sessionStorage.getItem(DISMISS_KEY) === '1')
  }, [])

  // "Has a plan to warn against": a stored one, or a staff comp in force
  // (AGL-3034), which grants a plan the stored field may not name at all.
  const planComp = resolvePlanComp(org)
  const plan = org?.plan || planComp?.plan

  // Host-level quotas — screens — plus every org capacity a plugin backs,
  // which today is the data plugin's datasets. Storage is not among them: it
  // is the workspace's band, read below.
  useEffect(() => {
    if (!orgInScope || !plan || !hostId) return
    let active = true
    const entitlements = resolveOrgEntitlements(org)
    /*
     * The capacities are COMPILED (AGL-3080), which is what lets this banner
     * read them at all. It renders on every console page, in a bundle that
     * loads no plugin code, and a registry no plugin here had filled would
     * answer "no capacities" — every quota row silently absent and the banner
     * reporting a clean account to a workspace that is over. That is AGL-3025
     * on a claim about the customer's account, so there is no registrar to
     * miss: the declarations are in the manifest before anything runs.
     *
     * Org-scoped, and skipped entirely for a site collaborator (AGL-1068) —
     * see `orgWideViewer`. They cannot be told an org-wide total, so the row
     * is omitted rather than shown as a wrong number. The
     * `hosts/{hostId}/datasets` fallback that used to sit here is gone
     * (AGL-1050): production held nothing under it and the rules deny it.
     */
    const capacities = pluginOrgCapacities()
    void Promise.all([
      getCountFromServer(
        collection(firestore, 'hosts', hostId, 'screens'),
      ).catch(() => null),
      Promise.all(
        capacities.map((capacity) =>
          orgWideViewer && orgId
            ? getCountFromServer(
                collection(firestore, 'orgs', orgId, capacity.collection),
              ).catch(() => null)
            : Promise.resolve(null),
        ),
      ),
    ]).then(([screens, counted]) => {
      if (!active) return
      setQuotas((previous) => [
        // The org-level rows — seats and the plugins' bands — are owned by
        // their own effects and survive a host change untouched.
        ...previous.filter(
          (quota) => quota.label === 'team seats' || quota.key !== undefined,
        ),
        {
          label: 'pages',
          used: screens?.data().count ?? 0,
          limit: entitlements.screensPerHost,
        },
        // No count, no row. Previously an unanswerable count fell through
        // to `?? 0`, which reads as "0 datasets used" — a confident wrong
        // number, and the one that would say you are comfortably under a
        // limit you may already have hit.
        //
        // Measured against the limit a create is refused at — what the plan
        // includes plus what the org bought, clamped to the plan's ceiling —
        // so the warning and the refusal name one number. Core resolves it
        // from the declaration's entitlement fields; the plugin names the
        // fields and never the figure.
        ...capacities.flatMap((capacity, index) => {
          const snapshot = counted[index]
          if (!snapshot) return []
          const used = snapshot.data().count
          return [
            {
              label: capacity.nouns.many,
              used,
              limit: checkPluginOrgCapacityQuota(org, capacity.kind, used)
                .limit,
            },
          ]
        }),
      ])
    })
    return () => {
      active = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firestore, hostId, orgId, plan, orgWideViewer, orgInScope])

  // Org-level team seats (AGL-238): the seat count is a client aggregate
  // query, cached per session.
  //
  // "The org roster is member-readable" stopped being true in AGL-1026 —
  // it is readable by ORG-WIDE members, because who else the workspace
  // employs is not a fact about the one site a collaborator was invited
  // to. The count went on being asked anyway and has been denied for every
  // scoped collaborator since, silently (AGL-1068). Nobody outside the org
  // roster gets a seat total; the row is omitted instead.
  useEffect(() => {
    // `orgInScope` first (AGL-1916). This is the effect that produced the
    // reported banner, and gating only the RENDER would leave it sending
    // `/api/orgs/members?orgId=…&counts=1` for the fallback org on every
    // mount of the picker — a request the page had no reason to make, about a
    // workspace nobody opened.
    if (!orgInScope || !plan || !orgId || !orgWideViewer) return
    let active = true
    const apply = (seats: number) => {
      if (!active) return
      const seatQuota = checkSeatQuota(org, 'managers', 0)
      setQuotas((previous) => [
        ...previous.filter((quota) => quota.label !== 'team seats'),
        { label: 'team seats', used: seats, limit: seatQuota.limit },
      ])
    }
    // Per-ORG cache key (AGL-1113). The key used to be a single global
    // string, so the seat count cached while viewing one workspace was
    // served to the next — a 2-manager org's total rendered against a
    // 2-seat Starter workspace's limit and put a false "you've reached your
    // team seats limit" banner on a workspace with one manager.
    const cacheKey = `${SEATS_KEY}:${orgId}`
    const cached = sessionStorage.getItem(cacheKey)
    if (cached != null) {
      apply(Number(cached))
      return
    }
    // Counted SERVER-side (AGL-1253). This used to be an unconstrained
    // `getDocs` on `orgs/{orgId}/members` — a list, so the rule's
    // `memberUid == request.auth.uid` clause could never satisfy it, and it
    // was measured denied on production for an account this component had
    // already decided was org-wide. Gating on `orgWideViewer` cannot fix
    // that: the client predicate and the rules predicate disagree about
    // legacy member docs, so the guard passes and the query still fails.
    //
    // MANAGERS only (AGL-1113) — the roster also holds site-scoped
    // collaborators, metered per host against membersPerHost. Counting the
    // whole collection is what put an "out of team seats" banner in front of
    // orgs that had only spent collaborator seats.
    void (async () => {
      try {
        const response = await authorizedFetch(
          user,
          `/api/orgs/members?orgId=${encodeURIComponent(orgId)}&counts=1`,
        )
        if (!response.ok) return void clearSeatCache(cacheKey)
        const payload = await response.json().catch(() => null)
        const seats = Number(payload?.managerSeats)
        // A number we could not read is not zero. `?? 0` here would render
        // "0 of 2 seats used" — a confident wrong answer, and the same trap
        // the datasets row above was fixed for.
        if (!Number.isFinite(seats)) return void clearSeatCache(cacheKey)
        sessionStorage.setItem(cacheKey, String(seats))
        apply(seats)
      } catch {
        // Network trouble: no seats row, and no stale one either.
        clearSeatCache(cacheKey)
      }
    })()
    return () => {
      active = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firestore, orgId, plan, orgWideViewer, user, orgInScope])

  // The token source, held in a ref rather than listed as a dependency. The
  // credits fetch below needs the user only to sign its request, and keying
  // the effect on the object's IDENTITY would re-issue the request — and
  // cancel the one in flight — on every render in which the provider hands
  // back a fresh object, which is every render under a hook that does not
  // memoize. The seats effect survives that by caching in sessionStorage;
  // a standing that must be fresh every mount cannot.
  const userRef = useRef(user)
  userRef.current = user

  // The plugins' bands that warn here (AGL-2898, AGL-3080) — the AI plugin's
  // assist credits — each from the console route its plugin declares, which
  // answers the standing in the band's own unit. Not a Firestore read: a
  // band's meter may be default-deny for every client (`assistUsage` is), and
  // the route answers in the unit the customer was sold, so no dollar figure
  // of ours crosses this boundary. Gated exactly as the seat count is: an
  // org-scoped route, and a viewer who can see org totals. A standing with
  // `limit: null` is a plan with no band, and no band is no row.
  useEffect(() => {
    if (!orgInScope || !plan || !orgId || !orgWideViewer) return
    let active = true
    for (const band of warnedBands()) {
      const warning = band.consoleWarning
      void (async () => {
        try {
          const response = await authorizedFetch(
            userRef.current,
            `${warning.standing}?orgId=${encodeURIComponent(orgId)}`,
          )
          if (!response.ok || !active) return
          const payload = await response.json().catch(() => null)
          const standing = payload?.[warning.member]
          // A standing with no band — an uncapped staff comp (AGL-3049) — is
          // no row, and `null` must not reach `Number()`, which reads it as 0.
          if (standing?.limit === null) return
          const used = Number(standing?.used)
          const limit = Number(standing?.limit)
          // A standing we could not read is not a standing of zero — the
          // datasets and seats rows hold the same line, for the same reason.
          if (!active || !Number.isFinite(used) || !Number.isFinite(limit)) return
          setQuotas((previous) => [
            ...previous.filter((quota) => quota.key !== band.id),
            {
              key: band.id,
              label: band.label,
              used,
              limit,
              sentence: bandSentence(warning, used, limit, payload?.stopsAtBand),
              linksUsage: warning.linksUsage === true,
            },
          ])
        } catch {
          // Network trouble: no row for the band, and no stale one either.
        }
      })()
    }
    return () => {
      active = false
    }
  }, [orgId, plan, orgWideViewer, orgInScope])

  /*
   * The workspace's storage band (AGL-3479), through the site's library —
   * whoever may upload into it may see the band it uploads against, which is
   * the route's own rule. On an org page there is no site, so it is asked
   * through the organization library, which the route answers for any member
   * of the org (AGL-3482). ONE request per scope opened, not per render, and
   * asked only where the banner may speak (`orgInScope`) and has a plan to
   * speak about. An unlimited band costs the route no read at all.
   */
  const storageBand = useMediaStorageBand({
    orgId: orgInScope && plan && !hostId ? orgId : null,
    hostId: orgInScope && plan ? hostId : null,
    user,
  })

  // ONE gate for every branch below (AGL-1916), placed above all three rather
  // than repeated inside them. Suspension and dunning are org claims of the
  // same kind as the quota rows — "this account is suspended", "your last
  // payment failed" — and read their fields off the same ambient object, so
  // the moment the fallback org carries `suspendedAt` or a past_due status the
  // picker announces it about a workspace the viewer did not open. Suspension
  // deliberately outranks plan and dismissal; it does not outrank knowing
  // WHICH org it is about.
  //
  // Every hook above has already run, so this early return costs no hook-order
  // hazard — and it is below them for exactly that reason.
  if (!orgInScope) return null

  // Suspension (AGL-202) outranks everything — not dismissible, shown
  // regardless of plan so pre-billing workspaces see it too.
  if ((org as any)?.suspendedAt) {
    return (
      <Alert severity="error" sx={{ borderRadius: 0 }}>
        {'This account is suspended' +
          ((org as any)?.suspendedReason
            ? ` — ${(org as any).suspendedReason}`
            : '') +
          '. Your published sites are offline. Contact support to resolve.'}
      </Alert>
    )
  }

  // Dunning (AGL-275): past_due is the grace window — entitlements keep
  // working, but the card needs fixing before the subscription dies.
  // Deliberately not dismissible; it clears when Stripe retries succeed.
  // Reads `billingStatus`, the bare status string the webhook mirrors onto the
  // org doc (AGL-1028). `subscription` itself moved to a manager-gated
  // subcollection, and this banner is shown to EVERY member on purpose — a
  // collaborator who cannot see the invoice still benefits from knowing the
  // workspace is about to lapse. Falls back to the inline object for orgs the
  // backfill has not reached.
  const billingStatus =
    (org as any)?.billingStatus ?? (org?.subscription as any)?.status
  // `unpaid` is NOT another shade of `past_due` — it is the grace window
  // having closed (AGL-1877). `DEAD_SUBSCRIPTION_STATUSES` in
  // plan-entitlements.ts contains `unpaid` and deliberately does not contain
  // `past_due`, so at the moment Stripe flips to it the workspace loses every
  // paid entitlement: hosts, seats, datasets, features. Matching `past_due`
  // alone meant this banner VANISHED on that flip — the warning disappearing
  // at precisely the instant the consequence arrived, leaving a workspace
  // running on Free with nothing on screen saying why.
  //
  // Same treatment, different sentence: the `past_due` copy promises access
  // continues while Stripe retries, which is exactly the thing that is no
  // longer true here, and repeating it would be worse than saying nothing.
  //
  // That copy used to name "the retry window" as though its length were a
  // known quantity (AGL-2430). The live window HAS since been read off the
  // Dashboard — 4 Smart Retries within 3 weeks, then cancel — and this copy
  // still does not quote it, deliberately. The retry schedule is a Dashboard
  // setting with no API surface at all, so the day somebody edits that
  // screen, a quoted number here becomes a lie and nothing goes red. See
  // `MAY_QUOTE_RETRY_WINDOW_IN_COPY` in utils/stripe-dunning-schedule.ts for
  // the full argument, and check:stripe-dunning-drift for the only thing
  // that watches the setting at all. So this says what is true under every
  // configuration that screen can hold: access continues while Stripe
  // retries, and the plan stops if the retries run out.
  // A subscription whose FIRST payment never completed — the customer's bank
  // asked to confirm it and the confirmation was abandoned or refused.
  //
  // A third state, before the two below, because both of their sentences are
  // actively false here. `past_due` promises access continues while Stripe
  // retries: nothing is retrying, and there is no access to continue since
  // the plan never started. `unpaid` says the payments failed and the plan
  // "has stopped": it never began.
  //
  // Entitlements are already correct — `incomplete` is in
  // `DEAD_SUBSCRIPTION_STATUSES`, so the workspace resolves to Free. That was
  // the whole defect: correct entitlements and NOTHING on screen saying why,
  // for a customer who believes they subscribed. They pressed Upgrade, their
  // bank asked a question, and from their side it simply did not work.
  //
  // `incomplete_expired` is deliberately NOT matched. Stripe expires these on
  // its own, and once it has there is nothing to complete — the subscription
  // is gone and the plan grid is the honest next step, not a banner nagging
  // about something unrecoverable.
  // A staff comp in force decides the plan on exactly these two statuses
  // (AGL-3034), so both sentences below would be false for it: the plan HAS
  // started, and the workspace is not running on Free. Staff granted it
  // knowing the subscription's state; the invoices stay on Billing.
  if (billingStatus === 'incomplete' && !planComp) {
    return (
      <Alert
        severity="warning"
        sx={{ borderRadius: 0 }}
        action={
          orgWideViewer ? (
            <AppLink
              componentVariant="button"
              color="inherit"
              size="small"
              href={buildRoute(Route.MANAGE_BILLING, { orgSlug })}
            >
              {'Finish payment'}
            </AppLink>
          ) : undefined
        }
      >
        {scopedViewer
          ? "This workspace's subscription is waiting on a payment that was " +
            'never completed, so its paid features have not started. A ' +
            'workspace admin needs to finish it.'
          : 'Your subscription is waiting on a payment that was never ' +
            'completed — your bank asked to confirm it and that step did not ' +
            'finish. Your paid plan has not started yet. Finish the payment ' +
            'from Billing, or it expires and you can start again.'}
      </Alert>
    )
  }

  const lapsed = billingStatus === 'unpaid' && !planComp
  if (billingStatus === 'past_due' || lapsed) {
    return (
      <Alert
        severity={lapsed ? 'error' : 'warning'}
        sx={{ borderRadius: 0 }}
        action={
          orgWideViewer ? (
            // Past due, the sentence asks for a new payment method, so the
            // button is named for that and lands on the Billing button that
            // does it (AGL-3442). Lapsed, the plan has already stopped, and
            // Billing's Outstanding card is where the unpaid invoice is paid.
            <AppLink
              componentVariant="button"
              color="inherit"
              size="small"
              href={
                lapsed
                  ? buildRoute(Route.MANAGE_BILLING, { orgSlug })
                  : updatePaymentMethodHref(orgSlug)
              }
            >
              {lapsed ? 'Fix billing' : 'Update payment method'}
            </AppLink>
          ) : undefined
        }
      >
        {lapsed
          ? scopedViewer
            ? "This workspace's payments failed and its plan has stopped. " +
              'A workspace admin needs to fix billing to restore paid ' +
              'features.'
            : 'Your payments failed and your plan has stopped — this ' +
              'workspace is running on Free until billing is fixed.'
          : scopedViewer
            ? "This workspace's last payment failed. A workspace admin needs " +
              'to update the payment method — access continues while Stripe ' +
              'retries, and the plan stops if the retries run out.'
            : 'Your last payment failed. Update your payment method to keep ' +
              'your plan — access continues while Stripe retries, and your ' +
              'plan stops if the retries run out.'}
      </Alert>
    )
  }

  if (!plan || dismissed) return null
  const storage = storageQuota(storageBand, scopedViewer)
  // The same point every Billing meter turns amber at.
  const breached = [...quotas, ...(storage ? [storage] : [])].filter(
    (quota) =>
      quota.limit !== UNLIMITED &&
      quota.limit > 0 &&
      (quota.used / quota.limit) * 100 >= USAGE_METER_WARNING_PCT,
  )
  if (!breached.length) return null
  const exceeded = breached.some((quota) => quota.used >= quota.limit)
  // A plugin band's row speaks for itself (AGL-2898); every other row shares
  // the one sentence below.
  const ownWords = breached.filter((quota) => quota.sentence)
  const others = breached.filter((quota) => !quota.sentence)
  const names = others.map((quota) => quota.label).join(' and ')

  return (
    <Alert
      severity={exceeded ? 'error' : 'warning'}
      sx={{ borderRadius: 0 }}
      action={
        // MUI's action slot replaces the onClose icon, so the dismiss
        // button lives beside Upgrade explicitly.
        <>
          {orgWideViewer && ownWords.some((quota) => quota.linksUsage) ? (
            // Where a band's stop and ceiling live (AGL-2898): the sentence
            // names the page, so the button takes the reader there.
            <AppLink
              componentVariant="button"
              color="inherit"
              size="small"
              href={buildRoute(Route.MANAGE_BILLING_USAGE, { orgSlug })}
            >
              {'Usage'}
            </AppLink>
          ) : null}
          {orgWideViewer ? (
            <AppLink
              componentVariant="button"
              color="inherit"
              size="small"
              href={buildRoute(Route.MANAGE_BILLING, { orgSlug })}
            >
              {'Upgrade'}
            </AppLink>
          ) : null}
          <Button
            color="inherit"
            size="small"
            onClick={() => {
              sessionStorage.setItem(DISMISS_KEY, '1')
              setDismissed(true)
            }}
          >
            {'Dismiss'}
          </Button>
        </>
      }
    >
      {/*
       * A site collaborator cannot buy anything, so "upgrade to keep adding"
       * is an instruction they cannot follow (AGL-1072). The warning itself
       * still earns its place — the rows they can see are this site's pages
       * and the workspace's storage, and they are the ones about to hit them
       * — so the limit stays and only the call to action changes. The org's
       * quotas are theirs to work within, not to act on.
       */}
      {[
        !others.length
          ? ''
          : scopedViewer
            ? others.some((quota) => quota.used >= quota.limit)
              ? `This site has reached its ${names} limit — ask a workspace ` +
                'admin to upgrade to keep adding.'
              : `This site is above ${USAGE_METER_WARNING_PCT}% of its ${names} quota.`
            : others.some((quota) => quota.used >= quota.limit)
              ? `You've reached your ${names} limit — upgrade to keep adding.`
              : `You're above ${USAGE_METER_WARNING_PCT}% of your ${names} quota.`,
        ...ownWords.map((quota) => quota.sentence),
      ]
        .filter(Boolean)
        .join(' ')}
    </Alert>
  )
}
QuotaWarningsBanner.displayName = 'QuotaWarningsBanner'

export default QuotaWarningsBanner
